import { localStore, type SettingsStore } from "../settings.ts";
import { CHAT_SIZES, chatSizeChoice, clampChatSize, writeChatSize, type ChatSize, type ChatSizeChoice } from "./size.ts";

/**
 * The page's chat size setting (P1.9-T42, ADR-061's amendment "the chat panel resizes"), as `HealthNumbersSetting`
 * holds the health numbers: made from what the store holds as the page loads, read by the chat panel, which follows
 * each change, and changed by the panel's grip and by Settings' Chat size, each change kept per viewer.
 *
 * @example
 * ```ts
 * const chatSize = new ChatSizeSetting(readChatSize(localStore()));
 * chatSize.setChoice("large");
 * ```
 */
export class ChatSizeSetting {
  private current: ChatSize;
  private written: ChatSize;
  private readonly listeners = new Set<() => void>();

  /**
   * @param size The size as the page loads, as `readChatSize` gives it.
   * @param store Where a change is kept; this browser's `localStorage` when omitted.
   */
  constructor(
    size: ChatSize,
    private readonly store: SettingsStore | null = localStore(),
  ) {
    this.current = clampChatSize(size);
    this.written = this.current;
  }

  /**
   * The size wished for now, within the range; what an area shows of it is `fitChatSize`'s.
   *
   * @returns The size, the same object until it changes.
   */
  readonly getSnapshot = (): ChatSize => this.current;

  /**
   * Follows the size.
   *
   * @param listener Told after each change.
   * @returns Stops following.
   */
  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * The choice the size is, for Settings.
   *
   * @returns Small, Medium or Large, or null for a size the grip left between them.
   */
  get choice(): ChatSizeChoice | null {
    return chatSizeChoice(this.current);
  }

  /**
   * Takes a size from the grip, which the panel shows at once.
   *
   * @param size The size, clamped to the range.
   * @param keep Whether to keep it: false while a drag is under way, so the store is written once, as it ends, and
   *   not at all when the size is the one the store already holds.
   */
  setSize(size: ChatSize, keep: boolean): void {
    const next = clampChatSize(size);
    if (next.width !== this.current.width || next.height !== this.current.height) {
      this.current = next;
      for (const listener of this.listeners) {
        listener();
      }
    }
    if (keep && (this.written.width !== this.current.width || this.written.height !== this.current.height)) {
      this.written = this.current;
      writeChatSize(this.current, this.store);
    }
  }

  /**
   * Takes Settings' choice, which the panel shows at once, and keeps it.
   *
   * @param choice Small, Medium or Large.
   */
  setChoice(choice: ChatSizeChoice): void {
    this.setSize(CHAT_SIZES[choice], true);
  }
}
